import { useEffect, useRef, useState } from "react";
import { Button, Modal, TextArea } from "@a2z/ui";
import { useTranslate } from "@/lib/i18n";
import {
  UnsavedChangesPrompt,
  useDiscardGuard,
} from "@/shared/UnsavedChangesPrompt";

/**
 * The kitchen-note editor for a basket line.
 *
 * Reachable only while the plates are DRAFT: once fired, the note is frozen —
 * the chit in the cook's hand must match the record — and the server refuses
 * the edit, so the chip that opens this simply is not rendered then. Saving an
 * empty text clears the note, which is why there is no separate delete button.
 */
export function NoteDialog({
  initialNote,
  isSubmitting,
  onCancel,
  onSave,
  dishName,
}: {
  initialNote: string | null;
  /** The dish the note is for, under the title (waiter phone, 2026-10-28). */
  dishName?: string;
  isSubmitting: boolean;
  onCancel: () => void;
  onSave: (note: string) => void | Promise<void>;
}) {
  const t = useTranslate();
  const [note, setNote] = useState(initialNote ?? "");
  // Focus on open: the dialog exists to type one line, so the caret should be
  // waiting in it. A ref+effect because the platform TextArea (correctly)
  // exposes no autoFocus of its own.
  const inputRef = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Typed work is anything that is not what the line arrived with — including
  // EMPTYING a note that had one, which is a real edit and the one a swipe
  // would most quietly undo.
  const guard = useDiscardGuard(note !== (initialNote ?? ""), onCancel);

  /*
   * The common asks as one-tap chips under the box (waiter phone, 2026-10-28):
   * «بدون بصل» is typed on a phone dozens of times a night. A chip adds its
   * words to the note, or takes them out again; the box stays for anything
   * else. Pressed = the note already says it.
   */
  const picks = t((m) => m.pos.noteQuickPicks)
    .split("|")
    .map((pick) => pick.trim())
    .filter(Boolean);
  const parts = note
    .split(/[،,]/)
    .map((part) => part.trim())
    .filter(Boolean);
  const togglePick = (pick: string) => {
    const next = parts.includes(pick)
      ? parts.filter((part) => part !== pick)
      : [...parts, pick];
    setNote(next.join(t((m) => m.pos.noteQuickJoin)).slice(0, 200));
    inputRef.current?.focus();
  };

  return (
    <>
    <Modal
      isOpen
      onClose={guard.requestClose}
      size="sm"
      title={
        dishName ? (
          <span className="flex flex-col">
            <span>{t((m) => m.pos.noteDialogTitle)}</span>
            <span data-testid="note-dish" className="text-[length:var(--font-size-xs)] font-normal text-[color:var(--text-muted)]">
              {dishName}
            </span>
          </span>
        ) : (
          t((m) => m.pos.noteDialogTitle)
        )
      }
      closeLabel={t((m) => m.common.close)}
      data-testid="note-dialog"
      footerActions={
        <>
          {/* «مسح الملاحظة» while there is text (the design's pair); the ✕
              closes. An empty box keeps «إلغاء». */}
          {note.trim() !== "" ? (
            <Button
              variant="outline"
              size="touch"
              data-testid="note-clear"
              onClick={() => {
                setNote("");
                inputRef.current?.focus();
              }}
            >
              {t((m) => m.pos.noteClearText)}
            </Button>
          ) : (
            <Button variant="outline" size="touch" onClick={guard.requestClose}>
              {t((m) => m.common.cancel)}
            </Button>
          )}
          <Button
            variant="primary"
            size="touch"
            className="font-extrabold"
            isLoading={isSubmitting}
            data-testid="note-save"
            onClick={() => void onSave(note)}
          >
            {note.trim() === "" && initialNote
              ? t((m) => m.pos.noteClear)
              : t((m) => m.pos.noteSave)}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <TextArea
          ref={inputRef}
          name="kitchenNote"
          value={note}
          onChange={(event) => setNote(event.target.value)}
          placeholder={t((m) => m.pos.notePlaceholder)}
          maxLength={200}
          rows={3}
        />
        <div className="flex flex-wrap gap-1.5" data-testid="note-picks">
          {picks.map((pick) => {
            const on = parts.includes(pick);
            return (
              <button
                key={pick}
                type="button"
                data-ui="chip"
                aria-pressed={on}
                data-testid="note-pick"
                onClick={() => togglePick(pick)}
                className={[
                  "rounded-full border px-3 py-1.5 text-[length:var(--font-size-xs)] font-bold transition-colors",
                  on
                    ? "border-[color:var(--accent)] bg-[color:var(--accent)] text-[color:var(--accent-contrast)]"
                    : "border-[color:var(--border-strong)] text-[color:var(--text-secondary)] hover:text-[color:var(--text)]",
                ].join(" ")}
              >
                {pick}
              </button>
            );
          })}
        </div>
      </div>
    </Modal>
    <UnsavedChangesPrompt
      isOpen={guard.asking}
      onDiscard={guard.discard}
      onKeepEditing={guard.keepEditing}
      testId="note-discard-prompt"
    />
    </>
  );
}
